import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createServerFn, useServerFn } from "@tanstack/react-start";
import { useState, useEffect } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getCrawlerConfig,
  updateCrawlerConfig,
  runAuditNow,
  type CrawlerConfig,
} from "@/lib/crawler-config.functions";

interface AuditRow {
  id: string;
  ran_at: string;
  ok: boolean;
  pass_count: number;
  fail_count: number;
  www_status: number | null;
  www_location: string | null;
  apex_status: number | null;
  apex_canonical_tag: string | null;
  apex_canonical_header: string | null;
  sitemap_status: number | null;
  sitemap_url_count: number | null;
  crawl_sample_size: number | null;
  crawl_failures: number | null;
  details: {
    top_issues?: string[];
    pages?: Array<{ url: string; status: number; ok: boolean; issues: string[]; hreflang_ok?: boolean }>;
  };
}

export const getCanonicalAuditRuns = createServerFn({ method: "GET" }).handler(async () => {
  const { requireAdminUnlockedServerSide } = await import("@/lib/admin-gate.server");
  await requireAdminUnlockedServerSide();
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("canonical_audit_runs")
    .select("*")
    .order("ran_at", { ascending: false })
    .limit(60);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AuditRow[];
});

export const Route = createFileRoute("/admin/canonical/")({
  head: () => ({
    meta: [
      { title: "Canonical Audit Dashboard — Optimize Plus" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    const { checkAdminUnlocked } = await import("@/lib/admin-gate.functions");
    await checkAdminUnlocked();
  },
  component: CanonicalDashboard,
});

function CanonicalDashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["canonical-audit-runs"],
    queryFn: () => getCanonicalAuditRuns(),
    refetchInterval: 60_000,
  });
  const configQuery = useQuery({
    queryKey: ["crawler-config"],
    queryFn: () => getCrawlerConfig(),
  });

  const runs = data ?? [];
  const latest = runs[0];
  const chartData = [...runs]
    .reverse()
    .map((r) => ({
      ts: new Date(r.ran_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      pass: r.pass_count,
      fail: r.fail_count,
      wwwOk: r.www_status === 301 ? 1 : 0,
      apexCanonicalOk: r.apex_canonical_tag?.startsWith("https://optimizeplus.net") ? 1 : 0,
    }));

  return (
    <div className="container mx-auto py-10 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Canonical Audit Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Daily runs of the canonical / redirect / sitemap / hreflang audit. Monitor apex vs www stabilization.
          </p>
        </div>
        <ExportControls runs={runs} />
      </div>

      {isLoading && <p>Loading…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}

      {latest && (
        <div className="grid gap-4 md:grid-cols-4">
          <StatCard label="Last run" value={new Date(latest.ran_at).toLocaleString()} />
          <StatCard
            label="Status"
            value={
              <Badge variant={latest.ok ? "default" : "destructive"}>
                {latest.ok ? "PASSING" : `${latest.fail_count} FAIL`}
              </Badge>
            }
          />
          <StatCard
            label="www → apex"
            value={
              <Badge variant={latest.www_status === 301 ? "default" : "destructive"}>
                {latest.www_status} {latest.www_location ? "→ apex" : ""}
              </Badge>
            }
          />
          <StatCard label="Sitemap URLs" value={`${latest.sitemap_url_count ?? "?"}`} />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Pass / fail trend (last {runs.length} runs)</CardTitle>
        </CardHeader>
        <CardContent style={{ height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="ts" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="pass" stroke="#16a34a" name="Passing checks" />
              <Line type="monotone" dataKey="fail" stroke="#dc2626" name="Failing checks" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Canonical stabilization signals</CardTitle>
        </CardHeader>
        <CardContent style={{ height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="ts" />
              <YAxis domain={[0, 1]} ticks={[0, 1]} />
              <Tooltip />
              <Legend />
              <Line type="stepAfter" dataKey="wwwOk" stroke="#0ea5e9" name="www returns 301" />
              <Line
                type="stepAfter"
                dataKey="apexCanonicalOk"
                stroke="#a855f7"
                name="apex canonical = apex"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <ConfigEditor config={configQuery.data} isLoading={configQuery.isLoading} />

      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>www</TableHead>
                <TableHead>apex canonical</TableHead>
                <TableHead>Sample</TableHead>
                <TableHead>Top issues</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{new Date(r.ran_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant={r.ok ? "default" : "destructive"}>
                      {r.pass_count} / {r.pass_count + r.fail_count}
                    </Badge>
                  </TableCell>
                  <TableCell>{r.www_status}</TableCell>
                  <TableCell className="max-w-[260px] truncate">
                    {r.apex_canonical_tag ?? "—"}
                  </TableCell>
                  <TableCell>
                    {(r.crawl_sample_size ?? 0) - (r.crawl_failures ?? 0)}/
                    {r.crawl_sample_size ?? 0}
                  </TableCell>
                  <TableCell className="max-w-[320px] text-xs text-muted-foreground">
                    {(r.details?.top_issues ?? []).slice(0, 2).join("; ") || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="text-lg font-semibold">{value}</CardContent>
    </Card>
  );
}

// ---------- Export (CSV / PDF, last 14 days) ----------

function last14Days(runs: AuditRow[]) {
  const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
  return runs.filter((r) => new Date(r.ran_at).getTime() >= cutoff);
}

function ExportControls({ runs }: { runs: AuditRow[] }) {
  const rows = last14Days(runs);

  function downloadCSV() {
    const header = [
      "ran_at",
      "ok",
      "pass_count",
      "fail_count",
      "www_status",
      "www_location",
      "apex_status",
      "apex_canonical_tag",
      "sitemap_status",
      "sitemap_url_count",
      "crawl_sample_size",
      "crawl_failures",
      "top_issues",
    ];
    const esc = (v: unknown) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")];
    for (const r of rows) {
      lines.push(
        [
          r.ran_at,
          r.ok,
          r.pass_count,
          r.fail_count,
          r.www_status,
          r.www_location,
          r.apex_status,
          r.apex_canonical_tag,
          r.sitemap_status,
          r.sitemap_url_count,
          r.crawl_sample_size,
          r.crawl_failures,
          (r.details?.top_issues ?? []).join(" | "),
        ]
          .map(esc)
          .join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    triggerDownload(blob, `canonical-audit-${todayStamp()}.csv`);
  }

  async function downloadPDF() {
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.setFontSize(16);
    doc.text("Canonical Audit — Last 14 days", 40, 40);
    doc.setFontSize(10);
    doc.text(
      `Generated ${new Date().toLocaleString()}  •  ${rows.length} run(s)  •  optimizeplus.net`,
      40,
      58,
    );

    autoTable(doc, {
      startY: 80,
      head: [["When", "Result", "www", "apex canonical", "Sample", "Top issues"]],
      body: rows.map((r) => [
        new Date(r.ran_at).toLocaleString(),
        `${r.pass_count}/${r.pass_count + r.fail_count}${r.ok ? " ✓" : " ✗"}`,
        String(r.www_status ?? "—"),
        r.apex_canonical_tag ?? "—",
        `${(r.crawl_sample_size ?? 0) - (r.crawl_failures ?? 0)}/${r.crawl_sample_size ?? 0}`,
        (r.details?.top_issues ?? []).slice(0, 3).join("; ") || "—",
      ]),
      styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
      headStyles: { fillColor: [15, 23, 42], textColor: 255 },
      columnStyles: { 3: { cellWidth: 140 }, 5: { cellWidth: 180 } },
    });

    doc.save(`canonical-audit-${todayStamp()}.pdf`);
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" size="sm" onClick={downloadCSV} disabled={!rows.length}>
        Export CSV (14d)
      </Button>
      <Button variant="outline" size="sm" onClick={downloadPDF} disabled={!rows.length}>
        Export PDF (14d)
      </Button>
    </div>
  );
}

function todayStamp() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------- Config editor ----------

function ConfigEditor({
  config,
  isLoading,
}: {
  config: CrawlerConfig | undefined;
  isLoading: boolean;
}) {
  const qc = useQueryClient();
  const update = useServerFn(updateCrawlerConfig);
  const runNow = useServerFn(runAuditNow);
  const [paths, setPaths] = useState("");
  const [cron, setCron] = useState("0 9 * * *");

  useEffect(() => {
    if (config) {
      setPaths(config.sample_paths.join("\n"));
      setCron(config.schedule_cron);
    }
  }, [config]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const sample_paths = paths
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      return update({ data: { sample_paths, schedule_cron: cron.trim() } });
    },
    onSuccess: () => {
      toast.success("Crawler config saved & schedule updated");
      qc.invalidateQueries({ queryKey: ["crawler-config"] });
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  });

  const runMut = useMutation({
    mutationFn: async () => runNow(),
    onSuccess: (res) => {
      if (res.ok) toast.success("Audit triggered — refreshing shortly");
      else toast.error(`Audit trigger failed: ${res.status}`);
      setTimeout(() => qc.invalidateQueries({ queryKey: ["canonical-audit-runs"] }), 4000);
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Crawler configuration</CardTitle>
        <p className="text-sm text-muted-foreground">
          Edit the sample URLs and cron schedule the daily audit uses. Changes reschedule the job automatically.
          {config?.updated_at && (
            <> Last updated {new Date(config.updated_at).toLocaleString()}.</>
          )}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-[1fr,320px]">
          <div className="space-y-2">
            <Label htmlFor="paths">Sample paths (one per line, must start with /)</Label>
            <Textarea
              id="paths"
              value={paths}
              onChange={(e) => setPaths(e.target.value)}
              rows={12}
              className="font-mono text-xs"
              disabled={isLoading}
              placeholder="/&#10;/about/&#10;/services/"
            />
            <p className="text-xs text-muted-foreground">
              Paths under /admin/ or /api/ are rejected. Duplicates removed on save.
            </p>
          </div>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="cron">Cron schedule (UTC)</Label>
              <Input
                id="cron"
                value={cron}
                onChange={(e) => setCron(e.target.value)}
                className="font-mono"
                placeholder="0 9 * * *"
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Standard 5-field cron. Examples: <code>0 9 * * *</code> daily 09:00 UTC,{" "}
                <code>0 */6 * * *</code> every 6h, <code>0 9 * * 1</code> Mondays 09:00 UTC.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || isLoading}>
                {saveMut.isPending ? "Saving…" : "Save & reschedule"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => runMut.mutate()}
                disabled={runMut.isPending}
              >
                {runMut.isPending ? "Running…" : "Run audit now"}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
